import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

// What each page last had picked, by page: the store codes, and the dashboard
// store it last followed. Held for this visit to the app (a reload starts
// clean, as Device Schedule's selection does), so leaving a page and coming
// back keeps its stores.
const remembered = new Map();

// The store picker's selection for a page: opens on the Dashboard's store, and
// after that keeps whatever is picked here.
//
// - First visit, or the Dashboard has moved to another store since this page
//   last followed it: the Dashboard's store is selected.
// - Otherwise, coming back to the page: the stores picked here last time are
//   put back, so a pick made here is not undone by visiting another page.
//
// Only fills an empty selection, and only once per visit to the page. Waits
// for `options` (matched on `value`, the store code), since the store list can
// arrive after the page opens. `pageKey` names the page's memory.
export const useDashboardStoreDefault = (pageKey, options, selected, setSelected) => {
  const houseId = useSelector((state) => state.dropdownSelectionData?.houseId);
  const done = useRef(false);

  useEffect(() => {
    if (done.current || options.length === 0) return;
    const memory = remembered.get(pageKey);
    const dashboardMoved = Boolean(houseId) && memory?.followedHouseId !== houseId;

    let next;
    if (memory && !dashboardMoved) {
      const byCode = new Map(options.map((option) => [String(option.value), option]));
      next = memory.codes.map((code) => byCode.get(code)).filter(Boolean);
    } else {
      const option = houseId && options.find((candidate) => String(candidate.value) === String(houseId));
      if (houseId && !option) return; // The list does not have it yet.
      next = option ? [option] : [];
    }

    done.current = true;
    remembered.set(pageKey, {
      followedHouseId: houseId ?? memory?.followedHouseId ?? null,
      codes: next.map((option) => String(option.value)),
    });
    if (next.length) setSelected((current) => (current.length ? current : next));
  }, [pageKey, houseId, options, setSelected]);

  // Remember every change made here, once the page has opened on its stores
  // (before that the selection is still the empty one it starts with).
  useEffect(() => {
    if (!done.current) return;
    const memory = remembered.get(pageKey) ?? { followedHouseId: houseId ?? null };
    remembered.set(pageKey, {
      ...memory,
      codes: selected.map((option) => String(option.value)),
    });
  }, [pageKey, houseId, selected]);
};
